import { inject, Injectable } from '@angular/core';
import { HubConnection, HubConnectionBuilder, HubConnectionState, IRetryPolicy, RetryContext } from '@microsoft/signalr';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { Subscription, } from 'rxjs/internal/Subscription';
import { Observable } from 'rxjs/internal/Observable';
import { environment } from '../../environments/environment';
import { SingletonCheckHelper } from '../helpers/singleton-check.helper';
import { Task } from '../helpers/task';
import { WebHubEventCaller } from '../models/api-events/web-hub-event-caller';
import { AuthenticatedUserViewModel } from '../models/logins';
import { AuthService } from '../services/auth.service';
import { ConfigService } from '../services/system/config.service';

class InfiniteRetryPolicy implements IRetryPolicy {
    nextRetryDelayInMilliseconds(retryContext: RetryContext): number {
        return Math.random() * 5000;
    }
}

@Injectable({
    providedIn: 'root'
})
export class TcmWebHubConnectionService {
    userIsAuthenticated$: Observable<boolean>;
    private readonly _userIsAuthenticated: BehaviorSubject<boolean>;

    private hubConnection: HubConnection;

    private connectingInterval: any;

    private isInitialized = false;
    private currentUserSubscription: Subscription;

    private hubUrl: string;

    private existingUser: number;

    private readonly authSvc = inject(AuthService);
    private configService = inject(ConfigService);

    constructor() {
        SingletonCheckHelper.ensureSingleton(this);
        this._userIsAuthenticated = new BehaviorSubject<boolean>(false);

        this.userIsAuthenticated$ = this._userIsAuthenticated.asObservable();

        this.configService.loadConfig().then(baseUrl => {
          if(baseUrl){
            console.log("Base Url -", baseUrl);
            const bUrl = (environment.production) ? baseUrl : "/";
            this.hubUrl = bUrl;
          }
        }).catch(err => {
            console.log(err);
        })
    }

    init() {
        if (this.isInitialized)
            return;

        this.isInitialized = true;

        this.currentUserSubscription = this.authSvc.currentUser$.subscribe(x => this.onCurrentUserChange(x));
        this.onCurrentUserChange(this.authSvc.currentUserValue);
    }

    on<T>(func: (x: WebHubEventCaller) => (arg: T) => void, handler: (arg: T) => void) {
        const res = func(WebHubEventCaller.instance);
        const name = res.name;

        this.hubConnection.on(name, handler);
    }

    buildHubConnection(){      
      if(!(this.hubConnection && this.authSvc.currentUserValue && this.existingUser === this.authSvc.currentUserValue.id)){         
        this.clearExistingUserHubConnection();        
        this.existingUser = this.authSvc.currentUserValue.id;
        this.hubConnection = new HubConnectionBuilder()
          .withUrl(`${this.hubUrl}events?UserId=${this.authSvc.currentUserValue ? this.authSvc.currentUserValue.id : ""}`, { accessTokenFactory: () => this.authSvc.currentUserValue.token })
          .withAutomaticReconnect(new InfiniteRetryPolicy())
          .build();
      }
    }

    clearExistingUserHubConnection(){      
      if(this.hubConnection){        
        this.hubConnection.stop();
      }
    }

    private async onCurrentUserChange(user: AuthenticatedUserViewModel) {      
        if (user && this.authSvc.currentUserValue) {
          this.buildHubConnection();
          this._userIsAuthenticated.next(true);
            if (this.connectingInterval)
                return;

            switch (this.hubConnection.state) {
                case HubConnectionState.Connected:
                case HubConnectionState.Connecting:
                case HubConnectionState.Reconnecting:
                    return;
                case HubConnectionState.Disconnecting:
                    await Task.delay(1000);
                    break;

            }

            if (this.hubConnection.state != HubConnectionState.Disconnected)
                return;

            this.connectingInterval = setInterval(async () => {

                if (this.hubConnection.state != HubConnectionState.Disconnected)
                    return;

                try {                  
                    await this.hubConnection.start();

                    clearInterval(this.connectingInterval);
                    this.connectingInterval = null;

                    console.log('Events hub: connection started')
                }
                catch (err) {
                    console.log('Events hub connection error: ' + err);
                }
            }, 2000);


        }
        else {
            if (this.connectingInterval) {
                clearInterval(this.connectingInterval);
                this.connectingInterval = null;
            }
            if(this.hubConnection){
              this.hubConnection.stop();
            }
        }
    }
}
